import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import PlayerPicker, { PlayerChips } from "./PlayerPicker";
import { errMsg, inputCls, pill } from "@/lib/scoring";

const todayCT = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });

function ClassList({ api, onPick }) {
  const [date, setDate] = useState(todayCT());
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get("/scoring/classes", { date }).then(setData); }, [date, api]);
  return (
    <div className="space-y-3">
      <input data-testid="class-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
      {!data && <Loader2 className="animate-spin text-slate-400" />}
      {data && !data.classes.length && <p data-testid="classes-empty" className="text-sm text-slate-400">No classes on this date.</p>}
      {data?.classes.map((c) => (
        <button key={c.session_id} data-testid={`class-${c.session_id}`} onClick={() => onPick(c)} className="w-full text-left rounded-xl border border-slate-200 px-4 py-3 hover:border-[#2563EB] transition-colors">
          <p className="text-sm font-bold text-[#0B0F19]">{c.title} <span className="text-slate-400 font-medium">· {c.age_group} · {c.time}</span></p>
          <p className="text-xs text-slate-500 mt-1">{c.players.length} registered player{c.players.length === 1 ? "" : "s"}</p>
        </button>
      ))}
    </div>
  );
}

export default function NewSession({ api, onCreated, onCancel }) {
  const [disc, setDisc] = useState("hitting");
  const [name, setName] = useState("");
  const [src, setSrc] = useState("class");
  const [players, setPlayers] = useState([]);
  const [meta, setMeta] = useState({});
  const [groups, setGroups] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [classIds, setClassIds] = useState([]);
  const [ages, setAges] = useState("");
  useEffect(() => { api.get("/scoring/groups").then((d) => setGroups(d.groups)); }, [api]);
  const merge = (list) => setPlayers((cur) => [...cur, ...list.filter((p) => !cur.some((c) => c.id === p.id))]);
  const pickClass = (c) => {
    merge(c.players);
    setClassIds(c.players.map((p) => p.id));
    setAges(c.age_group || "");
    setName((n) => n || c.title);
    setMeta({ program_id: c.program_id, class_session_id: c.session_id, age_group: c.age_group });
    if (c.program_type === "pitching") setDisc("pitching");
  };
  const create = async (draft = false) => {
    setErr(""); setBusy(true);
    try { onCreated((await api.post("/scoring/sessions", { discipline: disc, name, player_ids: players.map((p) => p.id), ...meta, age_group: ages || meta.age_group || "", draft })).id); }
    catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="new-session" className="rounded-2xl bg-white border border-slate-200 p-5 md:p-6 space-y-5">
      <div className="flex gap-2">{["hitting", "pitching"].map((d) => <button key={d} data-testid={`new-disc-${d}`} onClick={() => setDisc(d)} className={pill(disc === d)}>{d === "hitting" ? "Hitting" : "Pitching"}</button>)}</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="new-session-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Session name (optional)" className={inputCls} />
        <select data-testid="new-session-age" value={ages} onChange={(e) => setAges(e.target.value)} className={inputCls}><option value="">Age group (optional)</option><option>13U & Under</option><option>14U & Up</option></select>
      </div>
      <div>
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Add players from</p>
        <div className="flex flex-wrap gap-2">{[["class", "A class roster"], ["group", "A saved group"], ["pick", "Select players"]].map(([k, l]) => <button key={k} data-testid={`src-${k}`} onClick={() => setSrc(k)} className={pill(src === k)}>{l}</button>)}</div>
        <div className="mt-4">
          {src === "class" && <ClassList api={api} onPick={pickClass} />}
          {src === "group" && (groups.length ? groups.map((g) => (
            <button key={g.id} data-testid={`use-group-${g.id}`} onClick={() => { merge(g.players); setName((n) => n || g.name); setMeta((m) => ({ ...m, group_id: g.id })); }} className="mr-2 mb-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-[#0B0F19] hover:border-[#2563EB]">{g.name} · {g.players.length}</button>
          )) : <p className="text-sm text-slate-400">No saved groups yet — create one in the Groups view.</p>)}
          {src === "pick" && <PlayerPicker api={api} onPick={(p) => merge([p])} exclude={players.map((p) => p.id)} priorityIds={classIds} />}
        </div>
      </div>
      <div><p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Players in this session ({players.length})</p><PlayerChips players={players} onRemove={(id) => setPlayers((c) => c.filter((p) => p.id !== id))} /></div>
      {err && <p data-testid="new-session-error" className="text-sm font-semibold text-red-600">{err}</p>}
      <div className="flex gap-3">
        <button data-testid="start-session-btn" disabled={busy || !players.length} onClick={() => create(false)} className="rounded-full bg-[#2563EB] hover:bg-[#1d4fd7] disabled:opacity-40 px-6 py-3 text-sm font-bold text-white transition-colors">Start scoring</button>
        <button data-testid="save-draft-btn" disabled={busy || !players.length} onClick={() => create(true)} className="rounded-full border border-slate-300 disabled:opacity-40 px-6 py-3 text-sm font-bold text-[#0B0F19]">Save as draft</button>
        <button data-testid="cancel-new-session" onClick={onCancel} className="rounded-full border border-slate-300 px-6 py-3 text-sm font-bold text-slate-600 hover:text-[#0B0F19]">Cancel</button>
      </div>
    </div>
  );
}
